/**
 * Structured logger utility with leveled, prefixed output.
 * Lightweight wrapper around console providing consistent formatting
 * and JSON-safe error serialization.
 *
 * Usage:
 *   const log = createLogger('portal:image');
 *   log.info('Loaded', { count });
 */

export type LogLevel = 'error' | 'warn' | 'info' | 'debug' | 'trace';

interface LoggerOptions {
  /** Minimum level to emit (default derives from LOG_LEVEL / NODE_ENV) */
  level?: LogLevel;
  /** Custom time source for testing */
  now?: () => Date;
}

// Level ranking for filtering
const levelOrder: LogLevel[] = ['error', 'warn', 'info', 'debug', 'trace'];

// Resolve global log level once (env may be Vite style or Node)
const globalLevel: LogLevel = ((): LogLevel => {
  const raw = (typeof process !== 'undefined' && process.env && (process.env.LOG_LEVEL || process.env.VITE_LOG_LEVEL)) || '';
  const normalized = raw.toLowerCase() as LogLevel;
  if (levelOrder.includes(normalized)) return normalized;
  // Default: verbose in development, info otherwise
  const env = (typeof process !== 'undefined' && process.env && (process.env.NODE_ENV || process.env.VITE_NODE_ENV)) || 'production';
  return env === 'development' ? 'debug' : 'info';
})();

function shouldEmit(current: LogLevel, min: LogLevel): boolean {
  return levelOrder.indexOf(current) <= levelOrder.indexOf(min);
}

function serializeError(e: unknown) {
  if (e instanceof Error) {
    return {
      name: e.name,
      message: e.message,
      stack: e.stack,
    };
  }
  return e;
}

export interface PrefixedLogger {
  error(msg: string, meta?: unknown): void;
  warn(msg: string, meta?: unknown): void;
  info(msg: string, meta?: unknown): void;
  debug(msg: string, meta?: unknown): void;
  trace(msg: string, meta?: unknown): void;
  child(suffix: string): PrefixedLogger;
}

/** Create a prefixed logger */
export function createLogger(prefix: string, options: LoggerOptions = {}): PrefixedLogger {
  const minLevel = options.level || globalLevel;
  const timeSource = options.now || (() => new Date());

  function emit(level: LogLevel, msg: string, meta?: unknown) {
    if (!shouldEmit(level, minLevel)) return;
    const ts = timeSource().toISOString();
    const payload = meta === undefined ? '' : '\n' + safeStringify(meta);
    const line = `[${ts}] [${level.toUpperCase()}] [${prefix}] ${msg}${payload}`;
    switch (level) {
      case 'error':
        meta instanceof Error ? console.error(line, serializeError(meta)) : console.error(line);
        break;
      case 'warn':
        console.warn(line);
        break;
      case 'info':
        console.info(line);
        break;
      case 'debug':
        console.debug(line);
        break;
      case 'trace':
        console.trace(line);
        break;
    }
  }

  const api: PrefixedLogger = {
    error: (m, meta) => emit('error', m, meta),
    warn: (m, meta) => emit('warn', m, meta),
    info: (m, meta) => emit('info', m, meta),
    debug: (m, meta) => emit('debug', m, meta),
    trace: (m, meta) => emit('trace', m, meta),
    child: (suffix: string) => createLogger(`${prefix}:${suffix}`, options),
  };
  return api;
}

function safeStringify(meta: unknown): string {
  try {
    if (meta instanceof Error) return JSON.stringify(serializeError(meta));
    return JSON.stringify(meta);
  } catch (_e) {
    return '[Unserializable meta]';
  }
}

// Export a root logger for convenience
export const rootLogger = createLogger('app');

// Export default logger instance for backward compatibility
export const log = rootLogger;
